<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type {
  ConnectionFormData,
  ConnectionFormDataPerGroup,
  ConnectionFormDataPerPhase,
} from '../../models/Connection';
import PhaseNameInputOneTriple from './PhaseNameInputOneTriple.vue';
import PhaseNameInputThreeSingle from './PhaseNameInputThreeSingle.vue';
import { CONNECTION_TYPE, PHASE } from '../../utils/constants';
import type { SelectValue } from 'ant-design-vue/es/select';
import type { Rule } from 'ant-design-vue/es/form';
import { useCommissioningDeviceStore } from '../../stores/commissioningDeviceStore';
import type { Terminal } from '../../models/CommissioningDevice';

const { t } = useI18n();

const emit = defineEmits<{
  (e: 'update:connectionGroup', value: ConnectionFormDataPerGroup): void;
}>();

const props = defineProps<{
  groupNumber: number;
  formDataIndex: number;
  formData: ConnectionFormData;
  connectionGroup: ConnectionFormDataPerGroup;
}>();

const connectionTypeOptions = [
  { label: 'Single-phase connections', value: CONNECTION_TYPE.THREE_SINGLE },
  { label: 'Three phase connection', value: CONNECTION_TYPE.ONE_TRIPLE },
];

const commissioningDeviceStore = useCommissioningDeviceStore();
const hasPhaseNotConnected = computed(() => {
  const outputConnections = commissioningDeviceStore.outputConnections;
  return (
    outputConnections[`L1-${props.groupNumber}` as Terminal]?.ready === false ||
    outputConnections[`L2-${props.groupNumber}` as Terminal]?.ready === false ||
    outputConnections[`L3-${props.groupNumber}` as Terminal]?.ready === false
  );
});

const displayOneTripleInput = computed(
  () => props.connectionGroup.type === CONNECTION_TYPE.ONE_TRIPLE && !hasPhaseNotConnected.value,
);

const showInvalidTypeError = ref(false); // If user selected Three phase when it's invalid

/** When user changed connection type dropdown between ONE_TRIPLE and THREE_SINGLE */
const typeValidationRules: Rule[] = [
  {
    validator: async (_rule: unknown, value: CONNECTION_TYPE) => {
      if (value === CONNECTION_TYPE.ONE_TRIPLE && hasPhaseNotConnected.value) {
        showInvalidTypeError.value = true; // display the custom error block
        return Promise.reject('');
      }

      // clear error state
      showInvalidTypeError.value = false;
      return Promise.resolve();
    },
    trigger: 'change',
  },
];

const updateConnectionType = async (value: CONNECTION_TYPE) => {
  const canClearConnectionNames = hasPhaseNotConnected.value ? false : true;

  if (canClearConnectionNames) {
    emit('update:connectionGroup', {
      type: value,
      L1: { name: '' },
      L2: { name: '' },
      L3: { name: '' },
    });
  } else {
    // Still emit but keep the connection names
    emit('update:connectionGroup', {
      ...props.connectionGroup,
      type: value,
    });
  }
};

const updateSingleConnectionName = (phase: PHASE, value: ConnectionFormDataPerPhase) => {
  emit('update:connectionGroup', {
    ...props.connectionGroup,
    [phase]: value,
  });
};

const updateTripleConnectionName = (value: ConnectionFormDataPerPhase) => {
  emit('update:connectionGroup', {
    ...props.connectionGroup,
    L1: value,
    L2: value,
    L3: value,
  });
};
</script>

<template>
  <div ref="containerRef" class="connection-group-container">
    <a-form-item
      :name="[formDataIndex, 'connectionGroup', 'type']"
      :rules="typeValidationRules"
      class="form-item__type"
    >
      <a-select
        :value="connectionGroup.type"
        size="large"
        :data-tn="`connection-${groupNumber}-type-dropdown`"
        @change="(value: SelectValue) => updateConnectionType(value as CONNECTION_TYPE)"
      >
        <a-select-option
          v-for="option in Object.values(connectionTypeOptions)"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </a-select-option>
      </a-select>
    </a-form-item>

    <div v-if="showInvalidTypeError" class="error-container">
      <span class="error-title">All 3 connections must be live to select Three phase.</span>
      Safely shut down the system and check electrical connection(s). <br />
      Once restarted, press the 'Detect connections' button below.
    </div>

    <template v-if="displayOneTripleInput">
      <PhaseNameInputOneTriple
        :groupNumber="groupNumber"
        :formDataIndex="formDataIndex"
        :formData="formData"
        :connectionGroup="connectionGroup"
        :placeholder="`e.g. ${t('commissioning.configureConnections.placeholder')}`"
        @updateTripleConnectionName="updateTripleConnectionName"
      />
    </template>
    <template v-else>
      <PhaseNameInputThreeSingle
        ref="phaseNameInputRef"
        :groupNumber="groupNumber"
        :formDataIndex="formDataIndex"
        :formData="formData"
        :connectionGroup="connectionGroup"
        :placeholder="`e.g. ${t('commissioning.configureConnections.placeholder')}`"
        @updateSingleConnectionName="updateSingleConnectionName"
      />
    </template>
  </div>
</template>

<style lang="less" scoped>
.connection-group-container {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
}

.form-item__type {
  width: 100%;
  flex-grow: 1;
  margin-bottom: 1rem;
  padding: 0 @content-side-margin;

  :deep(.ant-form-item-explain.ant-form-item-explain-connected) {
    display: none;
  }
}

.error-container {
  color: @color-error;
  font-weight: @font-weight-regular;
  margin-bottom: 1rem;

  .error-title {
    font-weight: @font-weight-medium;
  }
}

:deep(.ant-select-selection-item) {
  font-size: 1rem;
  font-weight: @font-weight-medium;
  color: @color-primary;
}

:deep(.ant-input::placeholder) {
  font-style: italic;
  font-size: 1rem;
}

// Target border styling for the stacked inputs (for both oneTriple and threeSingle)
:deep(.name-input) {
  .ant-input-group-addon {
    background-color: @color-primary;
    color: white;
    font-weight: @font-weight-medium;
    border-color: @color-primary;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  .ant-input {
    border-color: @color-primary;
  }

  &.name-input__L2,
  &.name-input__L3 {
    .ant-input-group-addon {
      border-top: 1px solid @color-mid-tone;
    }
  }

  &.name-input__L1,
  &.name-input__L2 {
    .ant-input,
    .ant-input-group-addon,
    .ant-input-affix-wrapper {
      border-bottom: 0;
    }
  }
}

// Override on error state
:deep(.ant-form-item-has-error) {
  &.form-item__type {
    .ant-select:not(.ant-select-disabled):not(.ant-select-customize-input) .ant-select-selector {
      background-color: transparent;
    }
  }

  .ant-input,
  .ant-input-group-addon {
    border-bottom: 1px solid @color-error;
  }

  &.form-item__connection-name {
    :not(.ant-input-affix-wrapper-disabled):not(
        .ant-input-affix-wrapper-borderless
      ).ant-input-affix-wrapper {
      background-color: fade(@color-error, 20%);

      .ant-input {
        border: none;
      }
    }
  }
}
</style>
